import { useState } from 'react';
import {
  SelectInput,
  ApiErrorText,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Loader,
  NumberInput,
} from '@/shared/components';
import { getRequiredLabel, useSingleOptionAutoSelect } from '@/shared/lib';
import { NUMERIC_CONFIG } from '@/shared/config';
import { cn } from '@/shared/utils';
import type {
  CurrencyAmountFormFieldConfig,
  CurrencyAmountFormProps,
} from '@/shared/types/currencyAmountFormTypes.ts';

export function CurrencyAmountForm<
  TFieldValues extends Record<string, unknown> = Record<string, unknown>,
>({
  config,
  form,
  onSubmit,
  isLoading = false,
  error,
  disabled = false,
}: CurrencyAmountFormProps<TFieldValues>) {
  const [stickySubmitError] = useState<unknown>(null);

  if (!form) {
    return <div>Form is not available</div>;
  }

  const hasFormErrors = Object.keys(form.formState.errors).length > 0;
  const formError: unknown | undefined = stickySubmitError || error;

  const handleFormSubmit = (data: TFieldValues) => {
    if (hasFormErrors || disabled) {
      return;
    }
    onSubmit?.(data);
  };

  const renderField = (
    fieldConfig: CurrencyAmountFormFieldConfig<TFieldValues>
  ) => {
    const {
      name,
      type,
      label,
      placeholder,
      disabled: fieldDisabled,
      options,
      required,
    } = fieldConfig;

    return (
      <FormField
        key={name}
        control={form.control}
        name={name}
        render={({ field }) => {
          if (type === 'select') {
            const fieldValue = (field.value as string) || '';
            const { isDisabled } = useSingleOptionAutoSelect(
              options || [],
              fieldValue,
              field.onChange,
              fieldDisabled || disabled || options?.length === 0
            );

            return (
              <FormItem>
                <FormControl>
                  <SelectInput
                    {...field}
                    value={fieldValue}
                    floatingLabel={getRequiredLabel(label, required)}
                    options={options || []}
                    labelKey="name"
                    selectedKey="value"
                    disabled={isDisabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }

          if (type === 'input') {
            return (
              <FormItem>
                <FormControl>
                  <NumberInput
                    {...field}
                    isCustomElement
                    decimalScale={NUMERIC_CONFIG.precision.default}
                    value={field.value as number}
                    type="text"
                    inputMode="decimal"
                    label={getRequiredLabel(placeholder, required)}
                    onChange={field.onChange}
                    className="h-[6rem] rounded-3xl p3"
                    disabled={fieldDisabled || disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }

          return <></>;
        }}
      />
    );
  };

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form
          onSubmit={event => {
            event.preventDefault();
            form.handleSubmit(handleFormSubmit)(event);
          }}
          className={cn('flex flex-col gap-[3.2rem]', config.className)}>
          <div className="flex flex-col gap-[1.6rem]">
            <div className="flex flex-col gap-[1.6rem]">
              {config.fields.map(renderField)}
              {config.customContentAfterFields}
            </div>

            {config.customContentBeforeSubmit}
          </div>

          {formError ? (
            <ApiErrorText error={formError} className="py-0" />
          ) : null}

          {config?.showSubmitButton && (
            <div className="flex flex-col gap-[1.6rem]">
              <Button
                type="submit"
                variant="default"
                size="lg"
                className={cn('w-full', config?.submitButtonClassName)}
                disabled={isLoading}>
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader />
                  </div>
                ) : (
                  <>
                    {config.submitButtonText}
                    {config?.submitButtonIcon}
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
